import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Search, RefreshCw, ShieldAlert, AlertTriangle, Monitor, Ban, Mail, ShieldCheck, RotateCcw } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

interface Props {
  sessionToken: string;
  filterEmail?: string | null;
  onFilterClear?: () => void;
}

interface DownloadAttempt {
  id: string;
  message: string;
  reference_id: string | null;
  created_at: string;
  is_read: boolean;
}

interface BannedStudent {
  user_id: string;
  email: string;
  full_name: string;
  phone: string;
  revoked_count: number;
  last_revoked: string;
}

const ReportsTab = ({ sessionToken, filterEmail }: Props) => {
  const [reports, setReports] = useState<DownloadAttempt[]>([]);
  const [banned, setBanned] = useState<BannedStudent[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingBanned, setLoadingBanned] = useState(false);
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<"warn" | "ban" | "unban">("warn");
  const [selectedStudent, setSelectedStudent] = useState<{ userId: string | null; email: string; name: string } | null>(null);
  const [reason, setReason] = useState("");
  const { toast } = useToast();

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "list_download_attempts", sessionToken },
      });
      if (res.data?.error === "Session expired. Please log in again.") {
        toast({ title: "Session expired", variant: "destructive" });
        return;
      }
      if (res.data?.reports) setReports(res.data.reports);
    } catch {
      toast({ title: "Failed to load reports", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchBanned = async () => {
    setLoadingBanned(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "list_banned_students", sessionToken },
      });
      if (res.data?.banned) setBanned(res.data.banned);
    } catch {
      toast({ title: "Failed to load banned students", variant: "destructive" });
    } finally {
      setLoadingBanned(false);
    }
  };

  useEffect(() => { fetchReports(); fetchBanned(); }, []);
  useEffect(() => { if (filterEmail) setSearch(filterEmail); }, [filterEmail]);

  const filtered = reports.filter((r) => r.message?.toLowerCase().includes(search.toLowerCase()));
  const filteredBanned = banned.filter((b) =>
    b.email.toLowerCase().includes(search.toLowerCase()) ||
    b.full_name.toLowerCase().includes(search.toLowerCase())
  );

  const getAttemptType = (message: string) => {
    if (message.includes("screen recording") || message.includes("MediaRecorder") || message.includes("screen_recording")) return { label: "Screen Record", icon: Monitor, color: "bg-red-500/10 text-red-600" };
    if (message.includes("DevTools") || message.includes("F12") || message.includes("devtools")) return { label: "DevTools", icon: AlertTriangle, color: "bg-amber-500/10 text-amber-600" };
    if (message.includes("right-click") || message.includes("contextmenu")) return { label: "Right Click", icon: ShieldAlert, color: "bg-blue-500/10 text-blue-600" };
    return { label: "Violation", icon: ShieldAlert, color: "bg-muted text-muted-foreground" };
  };

  const parseMessage = (message: string) => {
    const nameMatch = message.match(/:\s*(.+?)\s*\(/);
    const emailMatch = message.match(/\(([^)]+@[^)]+)\)/);
    const typeMatch = message.match(/tried\s*"([^"]+)"/);
    return {
      name: nameMatch?.[1] || "Unknown",
      email: emailMatch?.[1] || "Unknown",
      attemptType: typeMatch?.[1] || "Unknown",
    };
  };

  const openActionDialog = (type: "warn" | "ban" | "unban", student: { userId: string | null; email: string; name: string }) => {
    setDialogType(type);
    setSelectedStudent(student);
    setReason(
      type === "warn" ? "Suspicious download/recording activity detected on your account."
      : type === "ban" ? "Repeated policy violations — unauthorized download/recording attempts."
      : "Access restored after review. Please follow the terms of service."
    );
    setDialogOpen(true);
  };

  const handleAction = async () => {
    if (!selectedStudent) return;
    setActionLoading(dialogType);
    try {
      const action = dialogType === "warn" ? "warn_student" : dialogType === "ban" ? "ban_student" : "unban_student";
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action, sessionToken, studentUserId: selectedStudent.userId, studentEmail: selectedStudent.email, reason },
      });
      if (res.data?.success) {
        const titles: Record<string, string> = { warn: "Warning sent", ban: "Student banned", unban: "Access restored" };
        toast({
          title: titles[dialogType],
          description: dialogType === "unban"
            ? `${selectedStudent.email}: ${res.data.restoredCount || 0} enrollment(s) re-activated`
            : `${selectedStudent.email}`,
        });
        setDialogOpen(false);
        if (dialogType === "ban" || dialogType === "unban") fetchBanned();
      } else {
        toast({ title: res.data?.error || "Action failed", variant: "destructive" });
      }
    } catch {
      toast({ title: "Action failed", variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  const violationCounts = new Map<string, number>();
  reports.forEach((r) => {
    const { email } = parseMessage(r.message);
    violationCounts.set(email, (violationCounts.get(email) || 0) + 1);
  });

  return (
    <div className="space-y-4">
      <Tabs defaultValue="violations">
        <TabsList>
          <TabsTrigger value="violations">
            <ShieldAlert className="w-4 h-4 mr-1" /> Violations ({reports.length})
          </TabsTrigger>
          <TabsTrigger value="banned">
            <Ban className="w-4 h-4 mr-1" /> Banned ({banned.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="violations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-destructive" />
                Download & Recording Attempts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search by name, email, type..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
                </div>
                <Button variant="outline" size="sm" onClick={fetchReports} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
                </Button>
              </div>

              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-12 space-y-2">
                  <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground/50" />
                  <p className="text-muted-foreground">No download attempts detected</p>
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Student</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Attempt</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Count</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((r, i) => {
                        const { name, email, attemptType } = parseMessage(r.message);
                        const category = getAttemptType(r.message);
                        const CategoryIcon = category.icon;
                        const count = violationCounts.get(email) || 1;
                        return (
                          <TableRow key={r.id} className={!r.is_read ? "bg-destructive/5" : ""}>
                            <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                            <TableCell className="font-medium">{name}</TableCell>
                            <TableCell className="text-sm">{email}</TableCell>
                            <TableCell className="text-sm">{attemptType}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className={`${category.color} gap-1`}>
                                <CategoryIcon className="w-3 h-3" />{category.label}
                              </Badge>
                            </TableCell>
                            <TableCell><Badge variant={count >= 3 ? "destructive" : "secondary"}>{count}x</Badge></TableCell>
                            <TableCell className="text-sm whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button size="sm" variant="outline" onClick={() => openActionDialog("warn", { userId: r.reference_id, email, name })}>
                                  <Mail className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => openActionDialog("ban", { userId: r.reference_id, email, name })}>
                                  <Ban className="w-3 h-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="banned" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2"><Ban className="w-5 h-5 text-destructive" /> Banned Students</span>
                <Button variant="outline" size="sm" onClick={fetchBanned} disabled={loadingBanned}>
                  <RefreshCw className={`w-4 h-4 mr-1 ${loadingBanned ? "animate-spin" : ""}`} /> Refresh
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingBanned ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
              ) : filteredBanned.length === 0 ? (
                <div className="text-center py-12 space-y-2">
                  <ShieldCheck className="w-10 h-10 mx-auto text-muted-foreground/50" />
                  <p className="text-muted-foreground">No banned students</p>
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Revoked</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredBanned.map((b) => (
                        <TableRow key={b.user_id}>
                          <TableCell className="font-medium">{b.full_name}</TableCell>
                          <TableCell className="text-sm">{b.email}</TableCell>
                          <TableCell className="text-sm">{b.phone}</TableCell>
                          <TableCell><Badge variant="destructive">{b.revoked_count} course(s)</Badge></TableCell>
                          <TableCell>
                            <Button size="sm" variant="default" className="gap-1"
                              onClick={() => openActionDialog("unban", { userId: b.user_id, email: b.email, name: b.full_name })}>
                              <RotateCcw className="w-3 h-3" /> Un-ban & Restore
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {dialogType === "warn" ? <><Mail className="w-5 h-5 text-amber-500" /> Send Warning to {selectedStudent?.name}</>
              : dialogType === "ban" ? <><Ban className="w-5 h-5 text-red-500" /> Ban {selectedStudent?.name}</>
              : <><RotateCcw className="w-5 h-5 text-green-600" /> Restore Access for {selectedStudent?.name}</>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Student Email</p>
              <p className="font-medium">{selectedStudent?.email}</p>
            </div>
            {dialogType === "ban" && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
                <p className="text-sm text-destructive font-medium">⚠️ This will:</p>
                <ul className="text-sm text-destructive/80 list-disc ml-4 mt-1 space-y-1">
                  <li>Revoke all course enrollments</li>
                  <li>Send a notification email</li>
                </ul>
              </div>
            )}
            {dialogType === "unban" && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-md p-3">
                <p className="text-sm text-green-700 font-medium">✅ This will:</p>
                <ul className="text-sm text-green-700/80 list-disc ml-4 mt-1 space-y-1">
                  <li>Re-activate ALL revoked enrollments to "approved"</li>
                  <li>Send a restoration email</li>
                </ul>
              </div>
            )}
            <div>
              <p className="text-sm font-medium mb-1">Reason / Message</p>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              variant={dialogType === "ban" ? "destructive" : "default"}
              onClick={handleAction}
              disabled={!!actionLoading || !reason.trim()}
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {dialogType === "warn" ? "Send Warning" : dialogType === "ban" ? "Ban Student" : "Restore Access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsTab;
